/* Generated from lib/guide/registry.ts by `npm run guide:locale`. Edit the registry's English,
 * then regenerate; translations live in locales/<lang>/guide.ts with the same keys. */
export const guide = {
  "chapters": {
    "getting-started": {
      "title": "Getting started",
      "description": "Sign in, set up your workspace and reach your first result."
    },
    "assets": {
      "title": "Assets & Sites",
      "description": "Register assets, group them into sites, and enrich them."
    },
    "inspections": {
      "title": "Inspections & maintenance",
      "description": "Check assets with checklists and keep repeat work on schedule."
    },
    "jobs": {
      "title": "Work & jobs",
      "description": "One list of what needs doing — then commission, assign and complete jobs."
    },
    "requests": {
      "title": "Requests",
      "description": "Report a problem once and track it to done in one case."
    },
    "contractors": {
      "title": "Contractors & Quotes",
      "description": "Bring in external contractors and compare quotes."
    },
    "reviews": {
      "title": "Reports, insight & compliance",
      "description": "Share reports, read the trends and prove compliance."
    },
    "team": {
      "title": "Team & Contacts",
      "description": "Add people, build teams and manage access."
    },
    "integrations": {
      "title": "Integrations & Automations",
      "description": "Connect other systems, automate processes and import your data."
    },
    "billing": {
      "title": "Billing & AI credits",
      "description": "Understand your plan, usage and AI credits."
    },
    "account": {
      "title": "Account & resilience",
      "description": "Stay productive offline and recover safely."
    }
  },
  "articles": {
    "welcome": {
      "title": "Welcome to AssetWRX",
      "summary": "What AssetWRX is and how this guide is organised.",
      "whyItMatters": "Everything connects to an asset, so a check, a repair or a document never lives in a silo — and this guide follows the way you actually work.",
      "timeEstimate": "2 min read",
      "intro": "AssetWRX is the operating system for your physical assets — a single place to register what you own, check its condition, commission work, and keep everyone in the loop.",
      "paragraphs": {
        "0": "Everything in AssetWRX hangs off an asset. From an asset you can run an inspection, raise a request, commission a job, attach documents and share access — and each of those stays linked to the asset so nothing loses its context.",
        "1": "The guide follows the operating loop shown at the top of this screen: add assets, inspect them, let people report issues, do the work, then review and share. Tap a stage on the loop to open its page, or How it works to read about it. Later chapters cover contractors, your team, integrations, billing and staying productive offline.",
        "2": "Many how-to articles have a \"Show me\" button — it runs an interactive walkthrough on the real screen, so you learn by doing. \"Guide me\" in the + menu offers walkthroughs for common tasks at any time."
      }
    },
    "finding-your-way": {
      "title": "Finding your way",
      "summary": "The side menu and the phone's More menu, group by group.",
      "whyItMatters": "Every place is one click away, grouped the way you work, so you never hunt for a page.",
      "timeEstimate": "1 min read",
      "intro": "On a tablet or a computer, the side menu on the left lists every place in AssetWRX, grouped the way you work. On a phone the same places are in the bottom bar and More.",
      "paragraphs": {
        "0": "Home is at the top. Work holds Work and Calendar. Manage holds Sites, Assets, Contacts, Checklists and Group rules. Insight holds Insights, Compliance and Knowledge.",
        "1": "Help and Account (your name and photo) stay at the bottom of the menu. On a short window the groups scroll, and Help and Account stay in view.",
        "2": "On a narrower screen, such as an iPad, the menu shows icons only, with a thin line between the groups. Tap the arrow at the top to open the menu with names over the page; pick a place and it closes. On a wide screen the same arrow widens or narrows the menu, and AssetWRX remembers your choice.",
        "3": "Anything your plan does not include is listed under Upgrade to use, with a lock. Tap it to see which plan includes it.",
        "4": "On a phone, Home, Assets and Work are in the bottom bar, and More uses the same groups as the side menu. Work holds Calendar. Manage holds Sites, Contacts, Checklists and Group rules. Insight holds Insights, Compliance, Knowledge and Activity. Anything your plan does not include is under Upgrade to use, and the Assistant, Help and Account are at the bottom."
      }
    },
    "signing-in": {
      "title": "Signing in",
      "summary": "Reaching your work quickly and securely.",
      "whyItMatters": "Security that stays out of your way — you land on the task you came for, even from a deep link, instead of a menu.",
      "timeEstimate": "2 min read",
      "intro": "Signing in is a quick gate, not a detour — AssetWRX takes you straight to what you came for.",
      "paragraphs": {
        "0": "Sign in with your email and password, or choose Continue with Google, Continue with Apple or Continue with Microsoft.",
        "1": "If you have turned on two-factor authentication (Account → Security), you enter the verification code from your authenticator app after your password.",
        "2": "If you follow a link to a specific record while signed out, AssetWRX remembers it and opens that exact screen once you have signed in.",
        "3": "Forgot your password? Tap Forgot your password? on the sign-in screen, then Send reset link. Open the email and choose a new password.",
        "4": "Change your email or password in Account → Security → How you sign in. Face ID or fingerprint sign-in is switched on or off there too, in the phone and tablet app only."
      }
    },
    "create-account-workspace": {
      "title": "Create your account & workspace",
      "summary": "The minimum setup to get a real result fast.",
      "whyItMatters": "Start getting value in minutes — you add structure like teams and sites only when you actually need it, never up front.",
      "timeEstimate": "3 min read",
      "intro": "Setup is two quick choices, then your first action — you are never trained on modules up front.",
      "paragraphs": {
        "0": "After sign-up, \"Set up your workspace\" asks what you manage — property, fleet, equipment, facilities or a mix — so AssetWRX can start you with checklists that suit it, and what to call your workspace. You can rename it or remove the starter checklists later.",
        "1": "Then choose your first useful outcome: Add my first asset, Try a practice inspection (with a sample asset you can remove at any time), or Go to Home. Starting with a real asset is the quickest path to value.",
        "2": "Home then shows a short \"Get started\" checklist — register an asset, share it, schedule an inspection or a job, invite contacts — alongside what needs you next. Teams are optional: add one later (see Team & Contacts) and move assets into it when you are ready."
      }
    },
    "add-your-first-asset": {
      "title": "Add your first asset",
      "summary": "Register something to work from — a name and a type are all you need.",
      "whyItMatters": "Your first asset unlocks everything else — inspections, jobs, requests, documents and AI insight all start from here.",
      "timeEstimate": "2 min",
      "intro": "Assets are the heart of AssetWRX. Adding one takes two decisions: a name, and what kind it is. Everything else can be filled in later.",
      "preview": {
        "screen": "Assets",
        "rows": {
          "0": "Pump 12",
          "1": "Boiler 3",
          "2": "Forklift A"
        },
        "highlight": "Add asset",
        "pin": "Tap to start"
      }
    },
    "assets-overview": {
      "title": "Working with assets",
      "summary": "Find or create an asset and take the best next action.",
      "whyItMatters": "A complete, always-current picture of what you own — with the single most useful next action one tap away, so admin never gets in the way.",
      "timeEstimate": "3 min read",
      "intro": "The Assets tab lists everything you manage, with Sites alongside. Open an asset to see its health, history, documents and the actions available on it.",
      "paragraphs": {
        "0": "The + menu on an asset holds everything you can do with it, on a phone, a tablet or a computer: Inspect, Add job, Report issue, Add plan, Add reading, Add document, Add article, Share, Add contact, Link asset, Move and Asset 360. Each stays linked to the asset. Guide me in the same menu walks you through any of them.",
        "1": "You do not need to fill in everything up front. Register an asset with a name and type now and enrich it (details, documents, site, relationships) as you go — richer data means better AI insight and reporting.",
        "2": "Search and Refine at the top of the list help you jump straight to an asset. Places, Equipment and Vehicles at the top switch between the three kinds; All shows everything, including Other.",
        "3": "Reports, beside Work on the asset page, lists every inspection and job report on the asset, newest first. All reports, Awaiting review and Signed off narrow it. A site has the same Reports tab."
      }
    },
    "places-equipment-vehicles": {
      "title": "Places, equipment and vehicles",
      "summary": "Buildings, floors and rooms are places; what is fitted in them is equipment; vehicles keep a home site.",
      "whyItMatters": "A hospital, hotel or data centre reads the way it is built: every room, rack and plant room in its building, and every piece of kit in the room it is in.",
      "timeEstimate": "3 min read",
      "intro": "Every asset is one of three kinds, or Other. A place is a building, wing, floor, room, ward, rack, car park or any other area. Equipment is what is fitted or used there. A vehicle moves, so it sits in nothing and keeps a home site instead.",
      "paragraphs": {
        "0": "When you add a place, pick what kind of place it is: Building, Floor, Room and the rest. Places you added before are places of the kind Property, so nothing you already have changes.",
        "1": "Each asset sits in one place at most, shown as Sits in on the form and Where it sits on the asset: Building A › Floor 3 › Room 301. A place sits only in another place. Equipment can sit in a place, a vehicle or another item, such as a pump inside a plant room or a tail lift on a van.",
        "2": "On a place, Add place inside and Add equipment here create the next level with Sits in already filled in. Move changes where something sits, and Take out leaves it in its site but inside nothing.",
        "3": "The Places list shows the tree: each place is indented under the one that holds it. The site counts places, equipment and vehicles separately, and every place, room included, is an asset on your plan.",
        "4": "Link asset still records other connections, such as what a pump depends on or what a sensor monitors. If an older link gave something two places, the asset says so and asks which one to keep."
      },
      "preview": {
        "screen": "Assets · Places",
        "rows": {
          "0": "Building A",
          "1": "  Floor 3",
          "2": "    Room 301"
        },
        "highlight": "Places",
        "pin": "One tree"
      }
    },
    "custom-asset-types": {
      "title": "Your own asset types",
      "summary": "Add types such as Chiller, Guest room or Infusion pump, each with the details it asks for.",
      "whyItMatters": "Every chiller records refrigerant and tonnage the same way, so reports, the AI assistant and Power BI can compare them.",
      "timeEstimate": "3 min",
      "intro": "Owners and admins open Account → Workspace → Asset types, on a phone, a tablet or a computer, and tap Add asset type. Give it a name, say whether it is a place, equipment, a vehicle or other, and add the details to fill in.",
      "paragraphs": {
        "0": "A detail can be text, a number with a unit, a date, a choice from a list, or yes or no. Mark a detail Must be filled in when every asset of the type needs it.",
        "1": "When you add or edit an asset, pick the type under Asset type (or under What kind of place for a place) and its details appear on the form. They show on the asset under Where it sits.",
        "2": "A type in use keeps its kind, and a detail that already holds values keeps its kind, so nothing recorded goes wrong. Archive a type you no longer add; assets that use it keep it and their details.",
        "3": "Checklists can be aimed at your types and at kinds of place: open the checklist, then Checklist details and visibility, and pick them under Your asset types or Kinds of place."
      },
      "preview": {
        "screen": "Asset types",
        "rows": {
          "0": "Name · Chiller",
          "1": "Refrigerant · Pick from a list",
          "2": "Tonnage · Number (t)"
        },
        "highlight": "Save",
        "pin": "Define it once"
      }
    },
    "set-up-a-building-in-one-go": {
      "title": "Set up a building in one go",
      "summary": "Make a whole building, floor by floor, from a preset such as Hotel or Hospital, and see what it adds to your bill first.",
      "whyItMatters": "A 300-room hotel takes one screen instead of an afternoon, and every room is on the right floor from the start.",
      "timeEstimate": "3 min",
      "intro": "On a site, tap Set up a building (it is also in the Assets list menu). Pick the kind of building, name it, and say how many floors there are and how many rooms, wards or units each floor has.",
      "paragraphs": {
        "0": "Each kind of building comes with the usual extras, such as a reception, gym and pools for a hotel, or a plant room on the top floor. Tick the ones you want. Shared places such as a car park sit at site level, not inside the building, and only show when the site does not have them yet.",
        "1": "Owners and admins can also add the usual asset types for that kind of building, such as Guest room, Lift and Fire door for a hotel, with their details. Types you already have are reused.",
        "2": "Before anything is made you see the list of places and a billing box: how many new assets this makes, how many you will have, and what your monthly cost becomes. Every place is a billed asset. If the total is more than your plan allows, Create stays off until you change plan or make fewer.",
        "3": "A mixed-use site, such as offices over shops, is set up one building at a time, each with its own kind. Something else gives plain floors and rooms.",
        "4": "To add a numbered run inside one place, open the place and tap Add many inside. Use # in the name pattern for the number: Room ## gives Room 01, Room 02."
      },
      "preview": {
        "screen": "Set up a building",
        "rows": {
          "0": "Kind of building · Hotel",
          "1": "Floors · 6",
          "2": "Room count per floor · 20"
        },
        "highlight": "Create",
        "pin": "Cost shown first"
      }
    },
    "rounds-and-place-rollups": {
      "title": "Rounds and what is inside a place",
      "summary": "Walk a floor or a building as one job with a list of stops, and see everything inside a place at a glance.",
      "whyItMatters": "A housekeeping or fire door walk is one job to assign and track, and each room keeps its own checklist answers.",
      "timeEstimate": "3 min",
      "intro": "On a place, tap Start a round. Pick the checklist, narrow the stops by kind, type or zone, untick any you want to skip, and give it a date if you like.",
      "paragraphs": {
        "0": "Start round makes one job on the place. Each stop is a check on that job, in order, and opening a stop starts an inspection on that stop's asset, so its answers and photos are kept on the room or item itself.",
        "1": "A round can have up to 200 stops. The job finishes like any other once its required checks are done.",
        "2": "A place that holds other assets shows a summary under Where it sits: how many places, equipment and vehicles are inside, how many are at high risk, and the open jobs and visits due. See all work inside opens the Work tab for the place and everything in it, grouped by asset."
      },
      "preview": {
        "screen": "Room round: Floor 3",
        "rows": {
          "0": "Stop 1: Room 301",
          "1": "Stop 2: Room 302",
          "2": "Stop 3: Room 303"
        },
        "highlight": "Start round",
        "pin": "One job, many stops"
      }
    },
    "create-a-site": {
      "title": "Create a site",
      "summary": "Group assets under a place — a building, estate or depot.",
      "whyItMatters": "Grouping by place makes reporting, hand-offs and accountability follow the real world — you manage a location, not a scattered list.",
      "timeEstimate": "2 min",
      "intro": "Open Sites (More → Sites on a phone), tap +, then Site. Only the name is needed; tap Create site. The site opens on Overview; its tabs are Overview, Details (its address, type, team and dates), Assets, Work, Reports and People.",
      "preview": {
        "screen": "Sites",
        "rows": {
          "0": "North Depot",
          "1": "Head Office"
        },
        "highlight": "Site",
        "pin": "Create a place"
      }
    },
    "add-assets-to-a-site": {
      "title": "Add assets to a site",
      "summary": "Move existing assets into a site.",
      "whyItMatters": "See and manage a whole location at once instead of one asset at a time — its assets, open work and risk in one place.",
      "timeEstimate": "1 min",
      "intro": "Open the site, tap +, then Assign asset and pick the asset. A message confirms it has moved. You can add more at any time.",
      "preview": {
        "screen": "North Depot",
        "rows": {
          "0": "12 assets",
          "1": "Team · Facilities"
        },
        "highlight": "Assign asset",
        "pin": "Assign assets"
      }
    },
    "attach-documents": {
      "title": "Attach documents",
      "summary": "Keep certificates, warranties and compliance docs on the asset.",
      "whyItMatters": "Never miss a certificate or warranty expiry — compliance lives on the asset with renewal reminders, so audits stop being a scramble.",
      "timeEstimate": "2 min",
      "intro": "Documents live on the asset, with a renewal reminder before they expire — so certificates and warranties never lapse unnoticed.",
      "paragraphs": {
        "0": "Open the asset, tap +, then Add document. Choose the Kind of document, add the file, and enter the expiry date if it has one.",
        "1": "To change a date or title later, open the document and tap Edit details. You do not need to upload the file again.",
        "2": "Certificates with an expiry date get a reminder before they run out, and show on the Compliance list."
      },
      "preview": {
        "screen": "Pump 12 · Documents",
        "rows": {
          "0": "Warranty.pdf",
          "1": "Gas safety certificate.pdf"
        },
        "highlight": "Add document",
        "pin": "Add a file"
      }
    },
    "add-knowledge": {
      "title": "Add knowledge to an asset",
      "summary": "Keep how-tos, FAQs and notes where the work happens.",
      "whyItMatters": "What one engineer learned stops leaving with them — the next person finds the reset procedure on the asset, not in someone’s head.",
      "timeEstimate": "2 min",
      "intro": "Knowledge is what your team knows about an asset: how-tos, FAQs, notes, links and files. On the asset, choose Guide me → Add knowledge, or Add article in the + menu.",
      "paragraphs": {
        "0": "Pick the type, give it a title people will search for, and write the content. Dictate it on site, or let AI polish rough notes into clear steps.",
        "1": "Knowledge search finds articles across every asset: More → Knowledge on a phone, or Knowledge in the side menu on a tablet or computer."
      },
      "preview": {
        "screen": "Boiler 3 · Knowledge",
        "rows": {
          "0": "Type · How-to",
          "1": "Title · Boiler reset procedure"
        },
        "highlight": "Save",
        "pin": "Write it once"
      }
    },
    "move-an-asset": {
      "title": "Record where an asset is",
      "summary": "Log a move, or correct a location that was wrong.",
      "whyItMatters": "A history of where things were — and why they moved — ends the search for a pump that was relocated last year.",
      "timeEstimate": "1 min",
      "intro": "When an asset moves, record it on the asset: choose Guide me → Record a move, or Move in the + menu.",
      "paragraphs": {
        "0": "Say whether it moved or was recorded wrong. A move keeps the old position in the history with when and why; a correction simply fixes the record."
      },
      "preview": {
        "screen": "Van 7 · Move",
        "rows": {
          "0": "It moved · Today",
          "1": "Where is it now? · Depot 2"
        },
        "highlight": "Record the move",
        "pin": "Moved or corrected"
      }
    },
    "relate-assets": {
      "title": "Relate assets",
      "summary": "Link assets so risk and criticality flow between them.",
      "whyItMatters": "See knock-on problems: when one asset has a problem, everything that depends on it shows it too.",
      "timeEstimate": "2 min",
      "intro": "Open the asset, tap +, then Link asset. Choose the other asset — what it belongs to, a part of it, or something it depends on — and tap Add relationship.",
      "preview": {
        "screen": "Pump 12 · Related",
        "rows": {
          "0": "Parent · Plant Room",
          "1": "Part · Motor unit"
        },
        "highlight": "Add relationship",
        "pin": "Link it"
      }
    },
    "assign-asset-to-team": {
      "title": "Assign an asset to a team",
      "summary": "Move an asset out of Personal so a team can work on it.",
      "whyItMatters": "The right people own the right assets, so work routes itself to the team instead of landing only on you.",
      "timeEstimate": "1 min",
      "intro": "Assets start in your personal workspace. Tap Change on the asset’s Responsible team to move it to a team, so everyone on that team can work on it.",
      "preview": {
        "screen": "Pump 12",
        "rows": {
          "0": "Responsible team · Personal",
          "1": "Site · North Depot"
        },
        "highlight": "Change",
        "pin": "Reassign owner"
      }
    },
    "take-an-asset-out-of-service": {
      "title": "Take an asset out of service",
      "summary": "Decommission with a reason and, if it needs it, a make-safe job.",
      "whyItMatters": "A boiler that is off for the winter should not go overdue, and nobody should find a job on it. Decommissioning pauses its schedules, blocks routine work and records why — and the make-safe job’s report is the decommissioning certificate.",
      "timeEstimate": "2 min",
      "intro": "An asset has two states: whether the record is live (active, archived, retired) and whether the thing is in service. Decommissioning changes the second: the asset stays in your register with its history, but it is out of service until you recommission it.",
      "paragraphs": {
        "0": "Leave “Make it safe first” on to create a job that makes the asset safe — isolate, secure, label, photograph. The asset shows “Decommissioning” until that job is checked and verified. Switch it off to take it out of service now; you then add a note saying who decided.",
        "1": "While an asset is out of service, its repeating work is paused and upcoming inspections are cancelled, with the reason. Its history and documents stay readable, and it still counts towards your plan: only retiring closes the record."
      },
      "preview": {
        "screen": "Boiler 3",
        "rows": {
          "0": "In service · Team Facilities",
          "1": "Next inspection · 12 Oct"
        },
        "highlight": "Decommission",
        "pin": "Under ⋯"
      }
    },
    "bring-an-asset-back-into-service": {
      "title": "Bring an asset back into service",
      "summary": "Recommission: inspection → jobs from findings → verified → in service.",
      "whyItMatters": "An asset only returns to service with proof: the inspection, and every job it led to, are saved on the asset as its certificate.",
      "timeEstimate": "3 min",
      "intro": "From the “Out of service” banner choose Recommission (or Commission, for an asset that was never in service). It opens a case that follows the inspection and jobs until the asset is back.",
      "paragraphs": {
        "0": "Start the inspection with any checklist. When it is complete, create jobs for anything that failed. Each job ends with a check that the problem is fixed.",
        "1": "Return to service becomes available once the inspection is complete and every job it raised is verified. An admin can waive an unresolved finding with a note, which goes on the certificate. Schedules resume from today."
      },
      "preview": {
        "screen": "Boiler 3",
        "rows": {
          "0": "Out of service · End of life",
          "1": "Schedules paused"
        },
        "highlight": "Recommission",
        "pin": "Start here"
      }
    },
    "retire-an-asset": {
      "title": "Retire an asset",
      "summary": "Close the record for good, with the disposal on file.",
      "whyItMatters": "A retired asset can never quietly get a job, a share or an invoice again — and the record of what happened to it (sold, scrapped, returned) is kept with its full history for the auditor.",
      "timeEstimate": "2 min",
      "intro": "Retire an asset that has left service with no work in flight. If it is still in service, the sheet offers to decommission it first; if jobs or inspections are open, it says which.",
      "paragraphs": {
        "0": "Pick the reason, add a note and the disposal — method, who took it, and the value. The sheet lists what will happen before you confirm: shares revoked, the public intake link switched off, schedules ended, no longer billed, hidden from lists and KPIs.",
        "1": "Type RETIRE to confirm. Retired assets appear under the Retired lens on the Assets list with everything still readable. Only a workspace admin can restore one, and it comes back out of service — recommission it to use it again."
      },
      "preview": {
        "screen": "Boiler 3",
        "rows": {
          "0": "Out of service · Replaced",
          "1": "No open work"
        },
        "highlight": "Retire asset",
        "pin": "Under ⋯"
      }
    },
    "run-an-inspection": {
      "title": "Run an inspection",
      "summary": "Check an asset against a checklist and capture evidence.",
      "whyItMatters": "Turn condition into evidence and action — catch issues while they’re cheap to fix, with an AI-generated checklist when you don’t have one yet.",
      "timeEstimate": "4 min",
      "intro": "Inspections run off a checklist. Choose Inspect from an asset’s + menu; AssetWRX suggests a matching checklist, and you start it Now or schedule it for Later.",
      "paragraphs": {
        "0": "No suitable checklist? Tap Change, then New checklist, and choose Generate with AI to have one drafted for this asset — or Create manually.",
        "1": "A scheduled inspection opens on Start, Details and, where changes need approval, Can’t do it now. Start goes straight in; when you finish you go back to the list you came from.",
        "2": "Afterwards, Confirm jobs lists every failed item and every repair a client approved, each with who does it and when. Change or Apply to all, then Create and assign. Draft with AI is optional and shows its credits first.",
        "3": "An inspection can go to a contractor: on a new one, choose A contractor under Work path; on one already made, Manage, Reassign, then Assign to a contractor. They need an AssetWRX account. A dated visit, and its repeats, appears in their My work and they start it there with the same checklist. You approve it as usual."
      },
      "preview": {
        "screen": "Inspection · Pump 12",
        "rows": {
          "0": "Checklist · General",
          "1": "When · Now"
        },
        "highlight": "Start inspection",
        "pin": "Begin the check"
      }
    },
    "photo-checks-with-vision": {
      "title": "Let photos fill in the checklist (WRX Vision)",
      "summary": "Take photos during an inspection and get suggested answers.",
      "whyItMatters": "Finish inspections faster: take the photos once, then accept or change what Vision suggests.",
      "timeEstimate": "3 min read",
      "intro": "During an inspection, tap Scan next to a checklist section or a reading, or Analyse photo with AI on an item. Take or pick the photos, and Vision suggests answers.",
      "paragraphs": {
        "0": "Nothing is saved until you accept it. Each suggestion shows what Vision saw. Accept it, change it, or leave it.",
        "1": "Some checks run on your device and use no credits. Checks that need the internet use AI credits, and the cost shows before you start.",
        "2": "No Scan button? Vision is not in your plan, or this checklist does not use photos for that section."
      },
      "preview": {
        "screen": "Inspection · Boiler 3",
        "rows": {
          "0": "Safety checks",
          "1": "Pressure reading"
        },
        "highlight": "Scan",
        "pin": "Take the photos"
      }
    },
    "proactive-maintenance": {
      "title": "Set up proactive maintenance",
      "summary": "Turn a one-off check into a recurring schedule.",
      "whyItMatters": "Stop firefighting — scheduled checks catch wear before it becomes a breakdown, which is where the real savings are.",
      "timeEstimate": "2 min",
      "intro": "Make a check come back on its own, so wear is caught before it becomes a failure. On the asset, tap +, then Guide me → Schedule recurring checks, or tap Inspect and pick Later.",
      "paragraphs": {
        "0": "Tap Later, pick the day and time of the first check, then under Repeat tap how often it comes back — for example Every month — and tap Schedule inspection. You can also set an end date.",
        "1": "For work that is due by use as well as by date — a service every 12 months or 10,000 miles — use a maintenance plan instead."
      },
      "preview": {
        "screen": "Inspection · Pump 12",
        "rows": {
          "0": "When · Later",
          "1": "Repeat · Every month"
        },
        "highlight": "Repeat",
        "pin": "How often"
      }
    },
    "maintenance-plans": {
      "title": "Maintenance plans & meter readings",
      "summary": "Repeat work by date, by use, or whichever comes first.",
      "whyItMatters": "\"12 months or 10,000 miles\" is one plan, not two — AssetWRX tracks both and raises the work when the first one arrives, so services are never missed or doubled.",
      "timeEstimate": "3 min read",
      "intro": "A maintenance plan is a service, test or inspection that repeats — by date, by meter, or whichever falls first. It raises the work itself when it is due.",
      "paragraphs": {
        "0": "On the asset’s Maintenance tab, tap Add plan. Turn on Repeat by date (every N hours, days, weeks, months or years — cleaning every 4 hours is one plan), Repeat by meter (mileage, run hours, cycles or another reading), or both — then AssetWRX works to whichever comes first. Say when it was last done; leave that blank if it never has been, and the first visit goes into Work straight away. Choose a job or an inspection (with a checklist), and who does it — or Decide each time.",
        "1": "Plans that count by use need readings. Tap Record reading on the asset’s Maintenance tab to enter one. In the phone or tablet app you can take a photo of the meter instead and the app reads the number, without using AI credits; on the web the photo is kept as evidence and you type the number. Readings save on the device when you are offline and sync later. A reading lower than the last one can only be saved as a correction, with a note saying why the earlier reading was wrong.",
        "2": "The asset’s Maintenance tab shows each plan’s status — due in 1,200 mi, due today, overdue — and Coming up shows the next few dates; a set of steps shows each step on a numbered line. A plan’s work appears in Work up to two weeks before it is due. With nobody named, you are asked to assign it when it arrives, and reminded three days before it is due."
      },
      "preview": {
        "screen": "Van 7 · Maintenance",
        "rows": {
          "0": "Service · Next at 40,000 mi or by 1 Mar",
          "1": "Mileage · 36,120 mi"
        },
        "highlight": "Record reading",
        "pin": "Keep it counting"
      }
    },
    "maintenance-steps": {
      "title": "Link plans as steps",
      "summary": "Put plans that belong together in order, like a 500, 1,000 and 2,000-hour service.",
      "whyItMatters": "A major service should not be raised while the smaller one before it is still open. Steps keep the work in the right order, and the asset shows them together as one set.",
      "timeEstimate": "2 min",
      "intro": "Steps are maintenance plans on one asset that belong together. Make each plan first with Add plan, then link them on the asset’s Maintenance tab.",
      "paragraphs": {
        "0": "On the asset’s Maintenance tab, tap Link plans as steps (it appears once the asset has two plans). Give the steps a name, then tap Add next to each plan, in the order they happen. Use the up and down arrows to change the order, and × to take a plan out again. Tap Save steps.",
        "1": "Leave Do the steps in order on and a step waits: its work is raised only when the step before it has no open work. Turn it off and each step comes round on its own schedule; they still show together as one set.",
        "2": "To change a set later, open it on the Maintenance tab and tap Edit steps. Add a step makes a new plan that joins the set as its last step. Unlink steps turns every step back into its own plan: nothing is deleted, and every plan keeps its schedule. An industry pack can also add a ready-made set of steps."
      },
      "preview": {
        "screen": "Generator 1 · Maintenance",
        "rows": {
          "0": "Generator services · 3 steps, done in order",
          "1": "Step 1 · Oil service"
        },
        "highlight": "Link plans as steps",
        "pin": "Done in order"
      }
    },
    "record-a-meter-reading": {
      "title": "Record a meter reading",
      "summary": "Keep plans that count by use on time.",
      "whyItMatters": "A service due every 10,000 miles is only on time if the mileage is known — one reading keeps every plan on that asset honest.",
      "timeEstimate": "1 min",
      "intro": "On the asset, choose Guide me → Record a meter reading, or Add reading in the + menu.",
      "paragraphs": {
        "0": "Type the number, or, in the phone or tablet app, add a photo of the meter and AssetWRX reads it on your device without using AI credits. On the web the photo is kept as evidence and you type the number. Offline readings sync when you are back online."
      },
      "preview": {
        "screen": "Van 7 · Reading",
        "rows": {
          "0": "Mileage",
          "1": "Reading · 36,120"
        },
        "highlight": "Save reading",
        "pin": "Keep it counting"
      }
    },
    "inspection-templates": {
      "title": "Checklists",
      "summary": "Build a checklist once and reuse it on every inspection.",
      "whyItMatters": "Standard checks mean comparable results — every inspector asks the same questions and captures the same evidence, whoever does the visit.",
      "timeEstimate": "3 min read",
      "intro": "Checklists are your library of reusable inspection checklists. Find them under Checklists: More → Checklists on a phone, or Checklists in the side menu on a tablet or computer.",
      "paragraphs": {
        "0": "Create a checklist and choose how to build it: Generate with AI drafts a checklist for you, or Create manually starts blank. Either way you can edit the result before saving.",
        "1": "Organise checks into groups. For each item choose how it is measured — condition, level, yes/no or pass/fail — mark it Required, set when it counts as a failure, and say what evidence to capture, such as a photo.",
        "2": "Search, filter by asset type or category, and reuse a checklist from any inspection: the planning sheet suggests the checklist that matches the asset.",
        "3": "To use a checklist only for some assets, open Checklist details and visibility and pick your own asset types (Chiller, Guest room) or kinds of place (Room, Floor). Left empty, it fits every asset of its kind."
      },
      "preview": {
        "screen": "Checklists",
        "rows": {
          "0": "Monthly fire safety",
          "1": "Vehicle walkaround"
        },
        "highlight": "Create checklist",
        "pin": "AI or by hand"
      }
    },
    "group-rules": {
      "title": "Group rules",
      "summary": "One checklist on a whole group of assets, on one schedule: every fire door in Building A, monthly.",
      "whyItMatters": "One rule replaces hundreds of separate schedules, and new assets that match are picked up without anyone remembering to add them.",
      "timeEstimate": "4 min",
      "intro": "A group rule says which assets, which checklist, how often and from when. Saving it makes one ordinary scheduled inspection for every asset in the group, so reminders, the planner and reports treat them like any other.",
      "paragraphs": {
        "0": "Open Group rules: More → Group rules on a phone, Group rules in the side menu on a tablet or computer, or Group rules at the top of Checklists. Everyone can see the rules; owners and admins add, edit and stop them. Add a group rule on a place opens the form already set to that place.",
        "1": "Say which assets: pick a kind (Place, Equipment, Vehicle or Other), then narrow it to a kind of place or one of your asset types, such as Fire door. Under Where, leave Anywhere or pick a place such as Building A to take only what is inside it, at every depth. A zone narrows it further. Only active assets are taken.",
        "2": "Pick the checklist, how often, and the date of the first inspection. You can also assign the inspections to one person, who is told once. The form reads the rule back in one sentence and says how many assets it takes right now.",
        "3": "Example: Monthly fire door check takes every Fire door inside Building A. Building A has 24 fire doors, so saving makes 24 inspections, all due on the first date. On the Work page they show as one row, Monthly fire door check · 24 assets; tap it to open the rule. When a door is done, its next monthly one is planned.",
        "4": "The rule keeps working after you save it. A new fire door added in Building A, or one moved there, gets its own inspection, due the day it joins. A door moved out, archived or changed to another type has its not-started inspection cancelled.",
        "5": "Editing a rule changes its inspections that have not started: a new checklist, how often, person or name is carried to them, a new first date moves the first ones, and assets that no longer match have theirs cancelled. Before you save, the form says how many will change. Started and finished inspections never change.",
        "6": "Stop a rule to cancel the inspections it made that have not started. Finished inspections and their results stay. Restart it to plan them again. Every inspection a rule made shows From group rule, with a link back to the rule."
      },
      "preview": {
        "screen": "Group rules",
        "rows": {
          "0": "Monthly fire door check",
          "1": "Fire door · inside Building A · Monthly",
          "2": "24 open inspections"
        },
        "highlight": "Add group rule",
        "pin": "One rule, many assets"
      }
    },
    "zones": {
      "title": "Zones",
      "summary": "Tag assets with zones that cut across places, such as Fire zone 2 or the East wing.",
      "whyItMatters": "A fire zone or a cleaning area rarely matches one building or floor. A zone lets a group rule or a round take exactly those assets.",
      "timeEstimate": "1 min",
      "intro": "A zone is a tag that cuts across places, such as Fire zone 2 or the East wing. Add zones on the asset form under Zones, separated by commas.",
      "paragraphs": {
        "0": "An asset can have several zones. A group rule matches a zone without regard to capitals, so East wing and east wing are the same zone.",
        "1": "Use a zone in a group rule to take only the assets tagged with it, or to narrow the stops of a round."
      },
      "preview": {
        "screen": "Edit asset",
        "rows": {
          "0": "Name · Fire door 3.12",
          "1": "Zones · Fire zone 2, East wing"
        },
        "highlight": "Save",
        "pin": "Tags, not places"
      }
    },
    "industry-packs": {
      "title": "Industry packs",
      "summary": "Start from checklists built for your trade.",
      "whyItMatters": "Day one looks like week ten — ready-made checklists for property, fleet, equipment or facilities, instead of a blank checklist library.",
      "timeEstimate": "2 min",
      "intro": "Industry packs add ready-made checklists for one industry. Open Account → Advanced → Industry packs, or Guide me → Add an industry pack.",
      "paragraphs": {
        "0": "Packs are starting points, not a compliance claim: edit the checklists to match how you work. Removing a pack keeps any checklist you have changed; checklists you never changed are removed with it."
      },
      "preview": {
        "screen": "Industry packs",
        "rows": {
          "0": "Property · 8 checklists",
          "1": "Fleet · 6 checklists"
        },
        "highlight": "Install pack",
        "pin": "Pick your trade"
      }
    },
    "work-list": {
      "title": "Your Work list",
      "summary": "Every request, inspection and job in one list, sorted by what needs you.",
      "whyItMatters": "One place to answer “what should I do next?” — urgent and overdue work rises to the top, whatever kind of work it is.",
      "timeEstimate": "2 min read",
      "intro": "The Work tab is one list of requests, inspections and jobs. It opens on Needs you, sorted by urgency.",
      "paragraphs": {
        "0": "Quick views change the lens: Needs you (overdue, urgent, or waiting on your decision), Mine (assigned to you or waiting on you), Due soon, Review (work and reports waiting for review or sign-off) and All.",
        "1": "Refine narrows the list by type (Requests, Inspections, Jobs), asset, site, assignee, urgency and status, and sets the sort and grouping. On a phone, the Calendar button (My Day with Work Planning) opens the calendar, which shows all planned work.",
        "2": "New requests, approvals and change requests all arrive here — open one to decide. Checklists live in their own Checklists library.",
        "3": "The scope chip at the top picks All or one team. If you also work for other companies as a contractor, choose Contractor work to see those jobs and quotes here. With more than one client, the work is grouped under each client’s name."
      },
      "preview": {
        "screen": "Work",
        "rows": {
          "0": "Boiler 3 · Inspection overdue",
          "1": "Pump 12 · Waiting for your approval"
        },
        "highlight": "Needs you",
        "pin": "Start here"
      }
    },
    "plan-a-day": {
      "title": "Plan a day",
      "summary": "Put the week's work onto people and times, see the travel between stops, and let Suggest a plan fill the rest.",
      "whyItMatters": "A planned day is one everyone can see and accept: who goes where, in what order, with what parts — and nothing is saved until you say so.",
      "timeEstimate": "4 min",
      "intro": "Work → the calendar button opens the Day board: a lane per person, the Unplanned queue beside it, and travel drawn between stops. Drag a card, or use its Plan panel, then Apply.",
      "paragraphs": {
        "0": "Unplanned lists work with no time or no person, each with a reason (No capacity, Part unavailable, Skill unavailable). Press one: the Plan panel asks who, which day and what time, and the card appears dashed on the board — suggested, not saved.",
        "1": "Suggest a plan places unplanned work for the least travel, inside each person's hours and skills, and gives a reason for every change. Tap × on any change you don't want. Apply saves the plan, tells each person once, and asks them to accept their day; a job past its hours or on top of another visit takes a reason.",
        "2": "The day board needs a tablet or computer and is for owners and admins. Everyone else sees My Day.",
        "3": "On a phone the same calendar is My Day: your visits in order with the trip between each, Accept and Decline, Directions, and Start next. Week shows how full each person's days are and whether forecast work would fit."
      },
      "preview": {
        "screen": "Work · Tue 6 Oct",
        "rows": {
          "0": "Sarah · 08:00 Pump 12 · Replace seal",
          "1": "John · 10:00 AHU 08 · Filter change",
          "2": "Unplanned · 9"
        },
        "highlight": "Suggest a plan",
        "pin": "Fill the day"
      }
    },
    "my-day": {
      "title": "Your day (My Day)",
      "summary": "See your visits for today in order, and start the next one.",
      "whyItMatters": "Know where to go next without asking: your visits in order, with the drive between them.",
      "timeEstimate": "2 min",
      "intro": "Open Work and tap My Day. Your visits for today are listed in order, with the drive between each.",
      "paragraphs": {
        "0": "Tap Accept to confirm your day, Directions to get there, and Start next to open the next job.",
        "1": "If your workspace uses it, Share location lets your planner see when you are running late. It only works while the app is open, and stops at the end of the day."
      },
      "preview": {
        "screen": "My Day · Tue 6 Oct",
        "rows": {
          "0": "08:00 Pump 12 · Replace seal",
          "1": "10:00 AHU 08 · Filter change"
        },
        "highlight": "Start next",
        "pin": "Begin the next visit"
      }
    },
    "create-a-job": {
      "title": "Create a job",
      "summary": "Commission work against an asset in a few taps.",
      "whyItMatters": "Every repair is tracked, costed and tied to its asset — no more work lost in inboxes or spreadsheets.",
      "timeEstimate": "3 min",
      "intro": "A job is a piece of work on an asset. Choose Add job from an asset’s + menu, describe it, and it lands in Work and on the asset’s history.",
      "preview": {
        "screen": "Job · Pump 12",
        "rows": {
          "0": "Title · Replace seal",
          "1": "When · Now"
        },
        "highlight": "Create job",
        "pin": "Commission it"
      }
    },
    "assign-a-job": {
      "title": "Assign a job",
      "summary": "Hand work to your team, a contractor, or out to quotes.",
      "whyItMatters": "Get work to the right hands fast — your team, a direct contractor, or out to competitive quotes — without leaving the job.",
      "timeEstimate": "2 min",
      "intro": "Under Who does it? choose Our team, A contractor or Get quotes. Then tap Assign on the job and pick the person or company. A contractor needs an email so they are told.",
      "paragraphs": {
        "0": "A job of yours due today offers Accept and start or Accept and schedule. Completing is one page: a note or a chip, photos, Submit. If the job needs costs, a meter reading or a check, the button says Continue.",
        "1": "A big job can hold sub-jobs: pieces with their own person or contractor and date, checked one by one and signed off once as a whole. See Jobs with sub-jobs. Work the same person does over several days is visits, not sub-jobs: see Visits."
      },
      "preview": {
        "screen": "Job · Pump 12",
        "rows": {
          "0": "Who does it? · A contractor",
          "1": "Assigned to · nobody yet"
        },
        "highlight": "Assign",
        "pin": "Pick who does it"
      }
    },
    "jobs-with-sub-jobs": {
      "title": "Jobs with sub-jobs",
      "summary": "Split a big job into pieces, each with its own person and date; check each, sign off once.",
      "whyItMatters": "A kitchen refit or a plant-room upgrade is several trades on several days. Sub-jobs keep them under one job, so you see what is left, who is late and what it costs, and the client signs off once.",
      "timeEstimate": "5 min read",
      "intro": "On the job, under Sub-jobs, tap Add sub-job. The first time, say who runs the job: you, a colleague, or a main contractor. Each sub-job is an ordinary job with its own person or contractor, date, checks, photos, costs and report.",
      "paragraphs": {
        "0": "The job shows its state once, worked out from its sub-jobs: Waiting, Scheduled, In progress, Needs you, Ready to sign off or Signed off. The Next step card says what to do now. Each sub-job sits on the list with its person, date and what it waits for; on a tablet or computer a timeline shows them by date with today and the forecast finish.",
        "1": "Turn on Do them in order to make each sub-job wait for the one before, or open a sub-job's menu and choose Waits for to pick exactly which. A sub-job that waits cannot start until the one it waits for is done and checked.",
        "2": "When a sub-job is finished, the job's owner checks it (or the checker you named), or sends it back, or asks for more photos. When every sub-job is checked, the job's approver signs off the whole job once. A job for a request is confirmed by the requester once, for the whole job.",
        "3": "Extra work found during a sub-job waits for a decision before the job can be signed off: Add to this job makes it the next sub-job; Separate job keeps it apart.",
        "4": "The menu (…) on Sub-jobs holds Move all dates, Set a budget, Save as a template, Use a template, Hand to a main contractor to run, and Cancel the whole job. A template keeps the steps, their order and what waits for what; never people or dates.",
        "5": "A main contractor can run the job for you: they add sub-jobs for their own crew, or for your contractors if you allow it, check each piece, and you sign off at the end. Take the job back at any time from the same menu.",
        "6": "On the Work page a job's sub-jobs fold under it; on the board each stays a card. From a sub-job, Back goes to its job. The job's report lists every sub-job (Summary or Full, Internal or For the client). Asset 360 and Site 360 count a job with sub-jobs once.",
        "7": "In Power BI, the Jobs table has SubJobOf, FamilyStatus, SubJobsTotal, SubJobsDone, SubJobsLate and CountsOnce. To count each job once, use a measure such as Jobs counted once = CALCULATE(COUNTROWS(Jobs), Jobs[CountsOnce] = TRUE()). The API lists a job's sub-jobs at /v1/jobs/{id}/sub-jobs, and webhooks tell you when a sub-job is added or a job's family state changes.",
        "8": "The assistant can tell you what is left on a job, who is late and the forecast finish, and move a sub-job and the ones after it by a number of days (you confirm, and can undo). Sub-jobs is a switch in your plan; switching it off keeps every sub-job already made."
      },
      "preview": {
        "screen": "Job · Kitchen refit",
        "rows": {
          "0": "Sub-jobs · 2 of 5 done",
          "1": "1. Strip out · Lee · done",
          "2": "2. First-fix electrics · Spark Ltd · today"
        },
        "highlight": "Add sub-job",
        "pin": "One job, many pieces"
      }
    },
    "visits": {
      "title": "Visits: one job over several days",
      "summary": "The same person comes back another day. Plan each visit; complete the job once, at the end.",
      "whyItMatters": "First fix Monday, second fix Thursday is one job, not two. Visits keep it on Work and the calendar on each day without closing and re-opening it.",
      "timeEstimate": "2 min",
      "intro": "On the job, under Visits, tap Plan a visit and pick the day and time. Each visit shows on Work and the calendar on its own day. On the day, tap I am here; add notes and photos for that visit; complete the job when the last visit is done.",
      "paragraphs": {
        "0": "The rule of thumb: the same person on more days is visits. A different person, trade, quote or check is a sub-job.",
        "1": "If you cannot finish today, open Can't complete as scheduled and choose I need to come back. Say why, add what was done, and pick the next visit. Today's visit ends, the next one is planned, and the job stays open and paused until then. Nobody needs to approve it.",
        "2": "The job's date always follows its next visit, so reminders and the planner keep working. A visit that has not started can be moved or cancelled from its menu. Completing or cancelling the job closes its open visits.",
        "3": "The API gives each job visitsTotal and nextVisits."
      },
      "preview": {
        "screen": "Job · Replace boiler",
        "rows": {
          "0": "Visit 1 · Mon 9:00 · Done",
          "1": "Visit 2 · Thu 9:00 · Planned"
        },
        "highlight": "Plan another visit",
        "pin": "Same person, another day"
      }
    },
    "prove-a-job-is-done": {
      "title": "Show a job is done (WRX Verified)",
      "summary": "Supply the photos and readings a job asks for, and get it checked.",
      "whyItMatters": "Fewer return visits: the person doing the job sees exactly what proof is needed, and the reviewer sees what the photos show.",
      "timeEstimate": "3 min read",
      "intro": "Open the job and look at What this job needs. Each row says what to supply, like a photo or a reading, with a button to add it.",
      "paragraphs": {
        "0": "When everything is supplied, tap Complete. AssetWRX checks the photos and tells the reviewer what they show.",
        "1": "The reviewer then taps Sign off, Send back, or Ask for photos. Only the reviewer can sign a job off.",
        "2": "Whoever created the job decides what proof it needs. They set it under Manage on the job."
      },
      "preview": {
        "screen": "Job · Replace valve",
        "rows": {
          "0": "Photo of the new valve",
          "1": "Pressure reading"
        },
        "highlight": "What this job needs",
        "pin": "Add each item"
      }
    },
    "cant-complete-as-scheduled": {
      "title": "When work can't go ahead",
      "summary": "Ask to move, reassign or skip scheduled work — with a reason.",
      "whyItMatters": "Blocked work is reported honestly instead of silently going overdue, and the manager decides with the reason and evidence in front of them.",
      "timeEstimate": "2 min read",
      "intro": "On a scheduled job you can't do as planned, choose Can't complete as scheduled. Pick what stopped you — no access, unsafe to proceed, parts unavailable and so on — and what you need.",
      "paragraphs": {
        "0": "Ask to Move it (same work, a later date), give it to Someone else, or Skip this one. Add detail and, ideally, a photo — it makes the request much easier to approve.",
        "1": "Moving or reassigning goes to your manager as a change request; nothing changes until they approve, reject, or change and approve it, and the work stays due meanwhile.",
        "2": "Where a plan allows it, skipping applies straight away. A skipped occurrence is recorded as skipped and keeps its place in the schedule — the next one is still due on time."
      },
      "preview": {
        "screen": "Can't complete as scheduled",
        "rows": {
          "0": "What stopped you? · No access",
          "1": "Add detail · Gate locked"
        },
        "highlight": "Send request",
        "pin": "Your manager decides"
      }
    },
    "report-an-issue": {
      "title": "Report an issue",
      "summary": "Log a problem once and choose what should happen.",
      "whyItMatters": "One report, the right outcome, one place to track it — nothing falls through the cracks between \"reported\" and \"fixed\".",
      "timeEstimate": "2 min",
      "intro": "Open the asset, tap +, then Report issue. Say what you need — Just help (not sure yet), A job done, or An inspection — add a short summary, and tap Submit. Someone reviews it in Work and turns it into work.",
      "paragraphs": {
        "0": "Dictate the details and use Tidy up with AI before you submit."
      },
      "preview": {
        "screen": "Report issue",
        "rows": {
          "0": "Asset · Pump 12",
          "1": "Short summary · Leaking seal"
        },
        "highlight": "What do you need?",
        "pin": "Choose the outcome"
      }
    },
    "convert-a-request": {
      "title": "Convert a request into work",
      "summary": "Approve a request and turn it into a job or inspection.",
      "whyItMatters": "A report becomes planned work in one tap, with the full history carried across — so the \"why\" always travels with the work.",
      "timeEstimate": "2 min",
      "intro": "Open the request from Work and tap Approve. Then tap Create a job or Inspect: the request’s details are filled in, and you choose who does it and when. Everything stays linked to the request.",
      "preview": {
        "screen": "Request · Pump 12",
        "rows": {
          "0": "Reported by · Tenant",
          "1": "Outcome · A job done"
        },
        "highlight": "Create work",
        "pin": "Approve → work"
      }
    },
    "share-for-requests": {
      "title": "Share an asset",
      "summary": "Give a contact controlled access to an asset.",
      "whyItMatters": "Share the right operational context without giving every contact the same permissions.",
      "timeEstimate": "2 min",
      "intro": "On the asset, tap + then Share, pick a contact, then review their access: Can view (read-only) or Can request, which also lets them report issues and follow their requests. Public QR intake is a separate option.",
      "preview": {
        "screen": "Pump 12 · Share",
        "rows": {
          "0": "Contact · Jo Bloggs"
        },
        "highlight": "Access · Can view",
        "pin": "Review access"
      }
    },
    "public-intake-qr": {
      "title": "Public intake with a QR code",
      "summary": "Let anyone report an issue by scanning a code.",
      "whyItMatters": "A QR on the asset turns anyone nearby into your early-warning system — reports arrive with the asset already identified.",
      "timeEstimate": "3 min",
      "intro": "Public intake lets anyone with the QR code report an issue on an asset — no app, no account, no login. Their reports arrive as requests in Work.",
      "paragraphs": {
        "0": "Someone who scans the code without signing in sees Report an issue only while public intake is on. With it off, the page offers Sign in to view."
      },
      "preview": {
        "screen": "Pump 12 · Public intake",
        "rows": {
          "0": "Public intake · On",
          "1": "Public intake QR code"
        },
        "highlight": "Copy link",
        "pin": "Share or print it"
      }
    },
    "add-a-contractor": {
      "title": "Add a contractor",
      "summary": "Add an external contractor you can assign work to.",
      "whyItMatters": "Send work to outside companies and track it in the same place as your own team’s work. They only see what you send them.",
      "timeEstimate": "2 min",
      "intro": "A contractor is a contact with the Contractor role. Only contacts with that role can be assigned jobs or invited to quote.",
      "paragraphs": {
        "0": "Under Roles, select Contractor before you tap Add contact."
      },
      "preview": {
        "screen": "New contact",
        "rows": {
          "0": "Name · Acme Repairs",
          "1": "Email · ops@acme.co"
        },
        "highlight": "Roles · Contractor",
        "pin": "Make assignable"
      }
    },
    "quotes-overview": {
      "title": "Getting quotes",
      "summary": "Request and compare quotes before awarding work.",
      "whyItMatters": "Compare like-for-like and award with confidence — without leaking one contractor’s pricing to another or exposing your wider workspace.",
      "timeEstimate": "3 min read",
      "intro": "When a job’s cost isn’t fixed, under Who does it?, choose Get quotes, invite contractors to price it, and award the one you choose.",
      "paragraphs": {
        "0": "Request quotes invites contractors from your contacts — it is not a public listing. Describe the work (AI can draft it), and if you like set a reply deadline, target dates and a budget.",
        "1": "Compare the quotes side by side, then tap Award on the one you choose. The job is given to that contractor with the price and time they quoted, and the others are told they were not chosen.",
        "2": "If the contractor you chose then declines the job, the comparison opens again with the other quotes back in it, so you can award someone else.",
        "3": "A contractor sees only what they need to price the work — never another contractor’s pricing or your wider workspace."
      }
    },
    "contractor-experience": {
      "title": "What your contractors see",
      "summary": "The contractor’s own view: invitations, their jobs, nothing else.",
      "whyItMatters": "Contractors work in AssetWRX in their own words — quote, accept, update, submit — while your internal notes and other contractors’ prices stay private.",
      "timeEstimate": "2 min read",
      "intro": "A contractor who signs in with the email you added them under sees only the work you send them.",
      "paragraphs": {
        "0": "Their Home shows invitations to quote or take on work. Their Work tab becomes My work, a pipeline: New, Upcoming, In progress, Awaiting review and Complete.",
        "1": "On a job they can send a price or decline, accept the work, post updates and photos, and submit it as complete. It waits in Awaiting review until you verify it or send it back.",
        "2": "They never see your team’s internal notes — the update composer marks them “Internal note — the contractor will not see this” — or another contractor’s pricing."
      }
    },
    "sign-off-an-inspection": {
      "title": "Sign off or send back an inspection",
      "summary": "Check a completed inspection, then sign it off or return it with a reason.",
      "whyItMatters": "Nothing is signed off by accident: a second pair of eyes approves the work, or sends it back saying exactly what to fix.",
      "timeEstimate": "2 min",
      "intro": "Open the completed inspection. In Approval, tap Sign off, or tap Send back and say what needs fixing.",
      "paragraphs": {
        "0": "The inspector gets your reason, and the inspection opens again for them with your note at the top. When they complete it again, it comes back to you.",
        "1": "Only the asset’s owner, its approver or its team can sign off, and never the inspector on their own work.",
        "2": "You can also sign off from the report: whoever signs it off sees Sign off and Send back at the top of the report. A job’s reviewer sees Sign off, Send back and Ask for photos at the top of the job’s completion report."
      },
      "preview": {
        "screen": "Inspection · Boiler 3",
        "rows": {
          "0": "Completed · awaiting sign-off",
          "1": "Approval"
        },
        "highlight": "Send back",
        "pin": "Or Sign off"
      }
    },
    "share-a-report": {
      "title": "Share a report",
      "summary": "Send an inspection report to the right contact.",
      "whyItMatters": "Close the loop — the person who cares sees the outcome, on the record, which is what turns a report into trust.",
      "timeEstimate": "2 min",
      "intro": "After an inspection, choose Share report. Pick the contact, set how they relate to the work — for example, the requester who raised it — and send it. Sharing closes the loop on a request.",
      "paragraphs": {
        "0": "If someone opens the link who is not the person it was sent to, they can tap Request access. The ask shows at the top of Work (Needs you and Review) and at the top of the report. Tap Approve to let them read that one report, or Decline.",
        "1": "Only the person you send it to can open it: the link in their email signs them straight in. The suggested repairs start approved; they tap Decline on any they do not want, sign, and the button on the signing screen is Sign off. Job reports and Asset 360 reports are shared with the same screen and words."
      },
      "preview": {
        "screen": "Share Report",
        "rows": {
          "0": "Report · Monthly safety",
          "1": "To · Jo Bloggs"
        },
        "highlight": "Send",
        "pin": "Close the loop"
      }
    },
    "ai-insight-reports": {
      "title": "Asset 360 and Site 360 reports",
      "summary": "One report on an asset or a site, written for the person who will read it.",
      "whyItMatters": "Turn scattered records into a decision-ready report with a clear verdict and an action plan, told the way your landlord, client, investor or site manager needs it.",
      "timeEstimate": "3 min",
      "intro": "Open the asset, tap +, then Asset 360. On a site, tap + then Site 360. The report opens straight away with the app's own figures: a verdict, a scorecard, each topic with its chart, and what to do next.",
      "paragraphs": {
        "0": "Pick who it is for: asset manager, client, landlord, site manager or investor. Every reader gets the same facts, in the order they care about. The landlord and investor versions hide contractor names unless you turn that off.",
        "1": "To have it written up, choose the language and the topics, then tap Write the AI version. It waits in a short queue and you are told when it is ready. Credits are used only once it is written; if it fails, nothing is charged.",
        "2": "The data quality score shows how complete the picture is, and Get more says what to record to make the next report stronger.",
        "3": "Each action in the plan has a button to raise the job or inspection that does it.",
        "4": "Versions keeps every saved version exactly as it was: tap Save this version (free) or write the AI version, then open any version to read it or compare it with the one before.",
        "5": "Tap Share to send a version to a landlord, client or anyone else, like an inspection report. They are told by e-mail and in the app, see the cover and scorecard straight away, and sign in or create a free account to read the rest. You can see when they read it, send them a newer version, or withdraw it.",
        "6": "Download PDF and Download CSV are for whoever approves work on the asset (for a site, on any of its assets).",
        "7": "How it compares sets each score against similar assets you can see (the same asset type) or your other sites, on last night's figures: better than most, about the same, or behind most.",
        "8": "Value and life shows when it was installed, how long it should last, what it cost, what replacing it would cost and what its jobs have cost, once you record them (Edit the asset, Value and life). When it is past its expected life, or a year of repairs costs half a new one, the plan suggests planning the replacement. With Look online on, the AI version can add a typical market price from online sources, marked as not checked.",
        "9": "Ask the assistant how an asset is doing or how it compares, and it answers from the same report; ask for a report for your landlord and it opens for that reader. Developers can read the report and its versions through the API, the AI agent connection (MCP) and the Power BI feed.",
        "10": "Your admin can switch Asset 360 and Site 360 on or off for each edition. When it is off, the button opens the upgrade page."
      },
      "preview": {
        "screen": "Pump 12 · Asset 360",
        "rows": {
          "0": "Verdict",
          "1": "Scorecard",
          "2": "Action plan"
        },
        "highlight": "Write the AI version",
        "pin": "For the reader you pick"
      }
    },
    "esg": {
      "title": "ESG and sustainability",
      "summary": "Energy, carbon, refrigerant (F-gas) and energy certificates for each asset, site and the whole workspace.",
      "whyItMatters": "Answer a landlord, investor or auditor with real figures, keep F-gas leak checks legal, and never let an energy certificate run out unnoticed.",
      "timeEstimate": "3 min",
      "intro": "Open an asset or a site and tap the ESG tab. It reads the meter readings you already record and turns them into energy, carbon and water for the last 12 months.",
      "paragraphs": {
        "0": "Energy comes from meters on the asset (electricity, gas, oil, heat or water). Each headline figure shows the change on the year before, the chart shows each month against the same month last year (tap a bar for its value), and Where the carbon comes from splits carbon by source. Carbon is an estimate using the UK government conversion factors for the year: scope 1 is fuel burnt on site and refrigerant leaks, scope 2 is electricity bought. Set a site's floor area on Edit site to see energy per m². Figures update overnight, or straight away with Refresh.",
        "1": "Refrigerant: the gas comes from the asset's Details, so record the charge on a cooling or heat pump asset. The app works out the tonnes CO2e and how often the law needs a leak check (every 12, 6 or 3 months, longer with leak detection) and plans the check as a maintenance plan, so it reminds you like any other. The Leak checks panel shows the band, the last result and Open job; the Leak log lists every check, leak, top-up and recovered gas. On a site, Book leak check raises the job.",
        "2": "Energy certificates are kept once, under Documents: add the EPC or DEC as a document and the AI reads the rating, score, reference and dates from it; the expiry date defaults to 10 years for an EPC and 1 year for a DEC. A certificate for the whole building goes on the site's Documents tab. The ESG tab shows it with Open certificate and Edit. Admins are reminded before it runs out, and a let building rated F or G is below the letting minimum and is flagged.",
        "3": "Write it up: pick a manager, landlord or investor and the AI writes a short summary that only uses these figures. Each suggestion has a button to raise the job, inspection or reading that does it.",
        "4": "Insights shows ESG for the whole portfolio, site by site. Asset 360 and Site 360 reports gain an ESG chapter, the assistant answers ESG questions, and developers can read it through the API, Power BI and the AI agent connection (MCP).",
        "5": "The Facilities industry pack adds two checklists, an energy walk-round and an F-gas leak check, and an energy certificate requirement for the compliance register.",
        "6": "ESG is an add-on with a free trial. Your admin turns it on for the workspace and can include every asset or only chosen sites. In the phone apps it is not available to buy in the app."
      },
      "preview": {
        "screen": "Head office · ESG",
        "rows": {
          "0": "Energy 120,000 kWh (+20%)",
          "1": "Carbon 25 t CO2e",
          "2": "EPC F · below the letting minimum"
        },
        "highlight": "Write it up",
        "pin": "From your own readings"
      }
    },
    "insights-overview": {
      "title": "Insights",
      "summary": "Trends, risk and performance across everything you look after.",
      "whyItMatters": "See what is deteriorating, where maintenance is slipping and who is overloaded — before it becomes a failure or a missed audit.",
      "timeEstimate": "2 min read",
      "intro": "The Insights tab turns your records into decisions. Switch between Technician, Manager and Executive views depending on the question you are asking.",
      "paragraphs": {
        "0": "Sections answer one question each: the key numbers, Condition (which assets are getting worse), Maintenance (where it is slipping), Forecast (what is coming), Risk, workload and contractor performance, and certificates running out in the next 90 days. Tap View details on a number to see the list behind it.",
        "1": "Workflows shows how requests, inspections and jobs move — where they get stuck and how long they take.",
        "2": "Write an AI summary turns the page into a few sentences. It uses AI credits, and the cost shows before you start.",
        "3": "Insights reads your existing data, so it gets sharper as your assets, inspections and jobs fill in. It is available on plans that include analytics."
      }
    },
    "compliance-register": {
      "title": "Compliance register",
      "summary": "Every requirement across your workspace — and what to do about it.",
      "whyItMatters": "Answer “are we compliant?” for the whole workspace in one list — and fix any gap from the same row.",
      "timeEstimate": "2 min read",
      "intro": "Compliance lists every requirement your assets have to prove, with its state: Compliant, Expiring, Overdue, Missing evidence or Unknown.",
      "paragraphs": {
        "0": "Open it from More → Compliance on a phone, Compliance in the side menu on a tablet or the web, or View compliance on an asset’s Compliance card. It covers a workspace, so switch out of Personal to see it.",
        "1": "Each row says who is responsible and offers the next step — Add evidence, Renew, Replace a rejected document, Remind validator (at most one reminder a day), Add expiry date (opens Edit details), or Inspect. Refine filters by status, site, team, asset type, requirement and due date.",
        "2": "Dates show where they came from — read from the document, suggested by AI, or entered by hand — and who accepted them."
      },
      "preview": {
        "screen": "Compliance",
        "rows": {
          "0": "Boiler 3 · Gas safety · overdue by 4 days",
          "1": "Van 7 · MOT · expires in 12 days"
        },
        "highlight": "Renew",
        "pin": "Fix it from the row"
      }
    },
    "create-a-team": {
      "title": "Create a team",
      "summary": "Set up a shared workspace for colleagues.",
      "whyItMatters": "A shared workspace means work, assets and history are visible to everyone who needs them — and to no one who doesn’t.",
      "timeEstimate": "2 min",
      "intro": "A team is a shared workspace. Go to Account → Teams & invites, create one, then invite people and assign assets to it so everyone can collaborate.",
      "preview": {
        "screen": "Team Management",
        "rows": {
          "0": "No Team"
        },
        "highlight": "Create Team",
        "pin": "Share a workspace"
      }
    },
    "invite-members": {
      "title": "Invite members",
      "summary": "Bring colleagues into a team as members or admins.",
      "whyItMatters": "Collaboration without oversharing — each person joins with exactly the access their role needs.",
      "timeEstimate": "2 min",
      "intro": "Team owners and admins can tap Invite people, enter one or more emails and choose Member (complete and update work) or Admin (manage people, roles and settings). Invitees get an email and appear once they accept.",
      "paragraphs": {
        "0": "If an invitation email does not arrive, use Copy invite link and send it yourself."
      },
      "preview": {
        "screen": "Team Management",
        "rows": {
          "0": "you@work.com · Admin"
        },
        "highlight": "Invite people",
        "pin": "Add colleagues"
      }
    },
    "transfer-team-ownership": {
      "title": "Transfer team ownership",
      "summary": "Hand the team to someone else, with their consent.",
      "whyItMatters": "Ownership never lands on someone by surprise, and a team is never left without an owner when people change roles.",
      "timeEstimate": "1 min",
      "intro": "The owner opens Team management and chooses Transfer ownership, then picks the member to offer it to.",
      "paragraphs": {
        "0": "The member sees the offer on the same screen and chooses Accept ownership or Decline. Nothing changes until they accept; you then become an admin. You can cancel a pending offer."
      },
      "preview": {
        "screen": "Team Management",
        "rows": {
          "0": "Settings",
          "1": "Transfer ownership"
        },
        "highlight": "Transfer ownership",
        "pin": "Offer, then accept"
      }
    },
    "contacts-overview": {
      "title": "Contacts & roles",
      "summary": "Add the right person without leaving your task.",
      "whyItMatters": "Add the right person in the flow of work, never a detour — and duplicate-detection keeps your directory clean.",
      "timeEstimate": "3 min read",
      "intro": "Contacts are the people and organisations you work with. You can add them inline wherever you need them — no detour to a separate screen.",
      "paragraphs": {
        "0": "A contact can carry several roles — such as Contractor, Supplier, Inspector, Tenant, Occupant or Driver. Roles decide where they can be picked: only Contractors can be assigned jobs or invited to quote.",
        "1": "When you add a contact inline (assigning a job, sharing an asset), AssetWRX checks for a possible existing match so you don’t create the same person twice.",
        "2": "What a contact can do on a shared asset is set by the share itself — Can view or Can request — not by their role.",
        "3": "Team members (people with a login in your workspace) are distinct from contacts (people you record and work with). See Create a team and Invite members for logins."
      }
    },
    "integrations-overview": {
      "title": "Connecting other systems",
      "summary": "Connect data, automate processes and import records by stating the result you want.",
      "whyItMatters": "Connect data and other systems by outcome, not connector expertise — and bring existing records in with a preview before anything changes.",
      "timeEstimate": "3 min read",
      "intro": "Integrations let AssetWRX exchange data with other systems. Workspace admins find them under Account → Integrations, starting from \"What do you want to achieve?\" — not from connector architecture.",
      "paragraphs": {
        "0": "Start simple: connect another business system, exchange files, or import records from a spreadsheet. Go further: react when something happens, automate a task, or connect sensors. Your IT team can also connect an AI assistant or build their own app.",
        "1": "The spreadsheet importer maps your columns to AssetWRX fields, previews exactly what will be created or updated, then applies it.",
        "2": "Your connections are listed under the outcomes. API reference, service accounts and keys, and step-by-step Guides & documentation sit under Developer & reference.",
        "3": "To show AssetWRX inside another portal, the workspace owner chooses Show AssetWRX in another portal under What do you want to achieve?"
      }
    },
    "plans-and-billing": {
      "title": "Plans & billing",
      "summary": "Understand your plan and manage it from one screen.",
      "whyItMatters": "See exactly what you’re paying for and manage it from one screen — with no false \"you’re subscribed\" until payment is truly confirmed.",
      "timeEstimate": "3 min read",
      "intro": "Account → Billing opens Billing & usage: your plan, billable assets, credits and consumption in one place. Only workspace admins can change commercial settings.",
      "paragraphs": {
        "0": "Your first active asset is free; adding more starts a subscription so billing can begin. You can change plan or manage your subscription from Billing & usage.",
        "1": "After returning from checkout, AssetWRX shows a pending state until your payment is confirmed in the background — a provider \"success\" screen never grants access on its own.",
        "2": "If you are not an admin, you can review usage but only an admin can change the plan or buy credits."
      }
    },
    "ai-credits": {
      "title": "AI credits",
      "summary": "How AI usage is measured — and why work never stops.",
      "whyItMatters": "Know your AI runway at a glance — and keep working even when credits run out, because AI only ever enhances the core flow, never gates it.",
      "timeEstimate": "2 min read",
      "intro": "AI features (generating checklists, polishing notes, insight reports and more) draw on AI credits. Your plan includes a monthly allowance, and admins can buy credit packs.",
      "paragraphs": {
        "0": "Billing & usage shows included and purchased credits, recent consumption and an estimated runway so there are no surprises.",
        "1": "Running out of credits only pauses the AI enhancement — never the underlying work. You can always complete assets, inspections, requests and jobs manually.",
        "2": "A failed AI request is not charged. Some features run on your device and use no credits at all — reading a meter from a photo, for example."
      }
    },
    "using-the-assistant": {
      "title": "Get things done with the assistant",
      "summary": "Ask questions, and have the assistant prepare changes you confirm.",
      "whyItMatters": "Ask in your own words, by typing, speaking or sending a photo, and the assistant looks things up and prepares the change. Nothing changes until you confirm it.",
      "timeEstimate": "3 min read",
      "intro": "Open the assistant from the header on a phone or tablet (on a phone it is also under More → Assistant), or with the button at the bottom right on a computer. Ask a question, or say what you want done: \"Add rooms 301 to 320 on Floor 3\", \"Move the Boiler 1 inspection to Friday\", \"What is overdue this week?\".",
      "paragraphs": {
        "0": "It can add and update assets, places, vehicles and sites, schedule and move inspections, create jobs, change their details, status and assignee, and add updates. Many rooms or items at once come as one card that says how many billable assets will be added.",
        "1": "Every change is a card. Check it, tap Edit to fix a name or a date, then Confirm. For 10 minutes afterwards you can tap Undo: something new is archived or cancelled, and a change goes back to how it was. The assistant never deletes anything.",
        "2": "It works as you: it only sees and changes what you could yourself, on your sites and with your sign-in checks. Changes made through it show \"via the assistant\" in the record's history and in the audit log. The reply appears as it is written, and while it checks your records you see how many it has looked up. Changes made through it also show on job and site history, with who confirmed them.",
        "3": "Tap the camera beside the microphone to send a photo of a nameplate, a meter or some damage. The assistant reads it once, copies any text exactly, and does not keep the photo.",
        "4": "It helps plan too. Ask \"Who is overloaded this week?\", \"When can Sam fit in a 2 hour job?\" or \"How can we improve our inspections?\" and it answers from working hours, time off and booked work, with the evidence for each suggestion. Ask it to plan someone's week, or to plan around someone being off, and it opens the planning board with that ready: you see the changes and the travel saved, and nothing moves until you tap Apply.",
        "5": "Ask how things are going: \"How has on-time completion moved?\", \"Which site cost the most this quarter?\", \"Why do we keep getting leaks at North Depot?\". Answers come from your records, with a small chart where it helps. When the conversation is empty, \"This week\" shows your week and a few questions to ask; hide it for the week or turn it off, and turn it back on in Assistant activity. Ask for a report and it opens the report screen ready to generate; reports cover your records as they stand today.",
        "6": "It knows where you are. Tick some assets in the list and ask \"Plan inspections for these\", and it works on exactly those. Tell it something to remember for the whole workspace, such as \"Gas safety jobs go to Northside Heating\", and it follows that from then on; everyone can see, change or forget these in Assistant activity. With no signal, your question waits and is sent when you are back online, and app guide articles that may answer it show straight away. Tap the speaker at the top to have replies read aloud, and turn on Hands-free to talk to it without touching the screen. Both work on the web now; the phone and tablet app gets them with its next store update.",
        "7": "Account → Workspace → Assistant activity lists every change made through the assistant. Owners and admins see everyone's and choose who may make changes through it: everyone, only admins, or nobody (it then answers and advises only).",
        "8": "Each message uses one AI credit; a long answer with several look-ups uses up to three, and a photo one more."
      },
      "preview": {
        "screen": "Assistant activity",
        "rows": {
          "0": "Create asset: Room 214 · Done",
          "1": "Reschedule inspection · Undone"
        },
        "highlight": "Who can make changes",
        "pin": "You confirm every change"
      }
    },
    "notifications": {
      "title": "Turn on notifications",
      "summary": "Get told when work is assigned to you or waits for you.",
      "whyItMatters": "Hear about new and urgent work straight away, without checking the app.",
      "timeEstimate": "1 min",
      "intro": "In the phone or tablet app, open the bell or Account → Preferences and tap Turn on notifications. Alerts go to the app only; on the web, the bell still lists everything.",
      "paragraphs": {
        "0": "If you said no before, tap Open settings and allow notifications for AssetWRX.",
        "1": "In Account → Preferences, choose what you are told about — Assigned to me, Messages, Approvals & requests — and set Quiet hours.",
        "2": "The bell lists everything in one place. Tap an item to open it, or snooze it to bring it back later."
      }
    },
    "request-a-feature": {
      "title": "Request a feature",
      "summary": "Tell the AssetWRX team what the app should do next, and follow what happens to it.",
      "whyItMatters": "The people who use AssetWRX every day know best what is missing. Every request is read, and you hear back.",
      "timeEstimate": "2 min",
      "intro": "Open Account → Help and tap Request a feature. Say what you would like and how it would help, pick the part of the app it is about and how much it matters.",
      "paragraphs": {
        "0": "You can add up to three screenshots. Leave out personal or customer details. Under Sent with your request you see exactly what goes with it: the app version, your device, your language and the screen you came from.",
        "1": "A request goes to the AssetWRX team only, never to your workspace or to other customers. If you asked for something like it before, the form shows it so you do not send it twice.",
        "2": "Your requests, under Help, lists what you sent with its status: Under review, Planned, Being built, In the app now or Not planned for now, with any reply from the team. You get a notification when it changes; turn Product updates off under Account → Preferences in the phone or tablet app if you would rather not.",
        "3": "Ideas we're considering lists ideas the team has written up. Tap I want this to vote, and you hear when the idea moves.",
        "4": "You can also tell the assistant \"I wish the app could…\". It drafts the request for you to send, or to edit first in the form."
      },
      "preview": {
        "screen": "Request a feature",
        "rows": {
          "0": "What would you like?",
          "1": "How would it help you?",
          "2": "Which part of the app? · Reports"
        },
        "highlight": "Send request",
        "pin": "Read by the team"
      }
    },
    "staying-productive-offline": {
      "title": "Working offline & recovering safely",
      "summary": "Keep working through connectivity loss and recover cleanly.",
      "whyItMatters": "Field work doesn’t stop when the signal does — your work is preserved and syncs when you’re back, and links never dead-end.",
      "timeEstimate": "3 min read",
      "intro": "AssetWRX is built to keep you moving through slow launches, lost connectivity and permission limits — your work is preserved and syncs when you are back online.",
      "paragraphs": {
        "0": "Supported actions save on your device and sync automatically when a connection returns; you can see their state as Saved locally, Syncing, Synced, Needs attention or Online required. When changes are waiting, Home offers Unsent changes, which lists everything waiting to be sent, with Send now.",
        "1": "A retryable failure never erases your input or evidence. Actions that genuinely need a connection tell you before you start, rather than losing your effort.",
        "2": "You can start, pause and complete a job, and pause and complete an inspection, with no signal. It shows Saved locally and is sent when you are back, checked again then; if someone else changed it meanwhile, it shows Needs attention instead of overwriting their change. You can also start a new inspection on an asset this device has opened before; it is sent when you are back.",
        "3": "Notification links open the exact record or, if it has moved or you can’t access it, a safe recovery screen — never a dead end. Read-only or unavailable features explain why and point you to the right next step."
      }
    }
  }
};
